import { Box } from "@mui/material";
import { KPIChart, KPIHeader, KPISummaryRow } from "./fragments";
import { useAtom } from "jotai";
import { userAtom } from "../../atoms";
import { useState, useEffect } from "react";
import { getDateRange } from "../../utils/dateUtil";
import { downloadFile, formatCurrency } from "../../utils";
import {
  useFetchKPIReportQuery,
  useFetchOrganizationInfoQuery,
} from "../../api";
import { KPIReportResponse, LeadMetric } from "../../types";
import moment from "moment";
import { Role } from "../../enums";

export const KPIReports = () => {
  const [user] = useAtom(userAtom);
  const [selectedOrgId, setSelectedOrgId] = useState(
    user?.roleCode === Role.Admin ? user.orgId || 0 : 0,
  );
  const [selectedOrgName, setSelectedOrgName] = useState("");
  const fetchOrgInfoQuery = useFetchOrganizationInfoQuery(user?.orgId || 0, {
    enabled: user?.roleCode === Role.Admin && !!user?.orgId,
  });

  useEffect(() => {
    if (
      user?.roleCode === Role.Admin &&
      fetchOrgInfoQuery.isSuccess &&
      fetchOrgInfoQuery.data
    ) {
      setSelectedOrgName(fetchOrgInfoQuery.data.name || "");
    }
  }, [fetchOrgInfoQuery.isSuccess, fetchOrgInfoQuery.data]);

  const currentDate = getDateRange("thisWeek");
  const [fromDate, setFromDate] = useState(
    moment(currentDate.startDate).toISOString(),
  );
  const [toDate, setToDate] = useState(
    moment(currentDate.endDate).toISOString(),
  );
  const orgId = selectedOrgId ?? 0;
  const fetchKPIReportQuery = useFetchKPIReportQuery(
    {
      orgId,
      accountId: 0,
      filters: {
        fromDate,
        toDate,
      },
    },
    true,
  );

  const kpiData = fetchKPIReportQuery.data as KPIReportResponse;
  const handleDownload = () => {
    if (!kpiData) return;

    const summaryRows = [
      [`KPI REPORT - ${selectedOrgName || "ALL ORGANIZATIONS"}`],
      [],
      ["FROM DATE", "TO DATE"],
      [
        moment(fromDate).format("MM/DD/YYYY"),
        moment(toDate).format("MM/DD/YYYY"),
      ],
      [],
      ["METRIC", "VALUE", "NO:OF INVOICES"],
      [
        "No. of Customers",
        `"${(kpiData.numberOfAccounts || 0).toLocaleString()}"`,
      ],
      ["Wash Count", `"${(kpiData.numberOfWashes || 0).toLocaleString()}"`],
      [
        "Average Wash Rate",
        `"${formatCurrency(kpiData.averagePricePerWash || 0)}"`,
      ],
      [
        "Average Revenue Per Customer",
        `"${formatCurrency(kpiData.averageRevenuePerAccount || 0)}"`,
        `"${(kpiData.numberOfInvoices || 0).toLocaleString()}"`,
      ],
      [
        "Average Units Per Customer",
        `"${(kpiData.averageWashesPerAccount || 0).toLocaleString()}"`,
      ],
      ["Gross Revenue", `"${formatCurrency(kpiData.grossRevenue || 0)}"`],
      [
        "Payments Received",
        `"${formatCurrency(kpiData.paymentsReceived || 0)}"`,
        `"${(kpiData.numberOfPaidInvoices || 0).toLocaleString()}"`,
      ],
      [
        "Payments Not Received",
        `"${formatCurrency(kpiData.outstandingBalance || 0)}"`,
        `"${(kpiData.numberOfUnpaidInvoices || 0).toLocaleString()}"`,
      ],
    ];
    const leadMetricRows = [
      [],
      ["LEAD NAME", "WASH COUNT", "REVENUE"],
      ...(kpiData.leadMetrics || []).map((item: LeadMetric) => [
        item.leadName,
        `"${(item.washesCount || 0).toLocaleString()}"`,
        `"${formatCurrency(item.revenue || 0)}"`,
      ]),
    ];

    const csvContent = [...summaryRows, ...leadMetricRows]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const link = URL.createObjectURL(blob);

    const fileName =
      [
        "KPI_report",
        fromDate?.replaceAll(":", "_") || "",
        toDate ? toDate.replaceAll(":", "_") : "",
      ]
        .filter(Boolean)
        .join("-") + ".csv";

    downloadFile(link, fileName);
  };

  const isLoading =
    fetchKPIReportQuery.isLoading || fetchKPIReportQuery.isFetching;

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        gap: 2.5,
      }}
    >
      <KPIHeader
        fromDate={fromDate}
        toDate={toDate}
        setFromDate={setFromDate}
        setToDate={setToDate}
        selectedOrgId={selectedOrgId}
        setSelectedOrgId={setSelectedOrgId}
        setSelectedOrgName={setSelectedOrgName}
        onDownload={handleDownload}
      />

      <KPISummaryRow data={kpiData} loading={isLoading} />

      <KPIChart data={kpiData} loading={isLoading} />
    </Box>
  );
};
